import React, { useEffect, useState } from 'react';
import ExportBar from '../components/ExportBar';
import { api, enablePush, pushStatus, pushSupported } from '../App';

// Full-page notification feed (replaces the MY ORDERS tab). Per-user, server-side
// read tracking. Viewing the page marks everything read. Order alerts open the
// order; the daily summary (link "view:home") jumps to the War Room.
export default function Notifications({ user, onOrderClick, onOpenView }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pstate, setPstate] = useState('off');  // 'on' | 'off' | 'blocked' | 'unsupported'
  const [pbusy, setPbusy] = useState(false);
  useEffect(() => { pushStatus().then(setPstate); }, []);
  async function togglePush() {
    setPbusy(true);
    try { setPstate(await enablePush()); }
    catch (e) { alert(e.message || 'Could not enable push'); }
    finally { setPbusy(false); }
  }

  const load = (markRead) => api('/notifications')
    .then(d => {
      const list = (d && Array.isArray(d.items)) ? d.items : [];
      setItems(list);
      setLoading(false);
      if (markRead) {
        const ids = list.filter(i => !i.is_read).map(i => i.id);
        if (ids.length) {
          api('/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) }).catch(() => {});
        }
      }
    })
    .catch(() => { setItems([]); setLoading(false); });

  useEffect(() => { load(true); }, []);

  const sevColor = s => s === 'red' ? 'var(--red)' : s === 'amber' ? 'var(--yellow)' : 'var(--teal)';
  const fmtWhen = ts => {
    if (!ts) return '';
    const d = new Date(String(ts).replace(' ', 'T'));
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };
  function clickItem(i) {
    if (!i.link) return;
    if (String(i.link).startsWith('view:')) onOpenView && onOpenView(i.link.slice(5));
    else onOrderClick && onOrderClick({ tas_no: i.link });
  }

  return (
    <div className="screen">
      <div className="header">
        <div><div className="header-logo">RISHAB</div><div className="header-sub">Rishab World Private Limited</div></div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{user?.name}</div>
          <div style={{ fontSize: 9, letterSpacing: 2, color: 'var(--text3)' }}>NOTIFICATIONS</div>
        </div>
      </div>

      <div style={{ padding: '14px 16px 90px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--gold)' }}>Notifications</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{items ? items.length : 0} total</div>
          {pushSupported() && (
            pstate === 'on' ? (
              <span style={{ fontSize: 11, color: 'var(--green)', border: '1px solid var(--green)', borderRadius: 4, padding: '4px 10px' }}>🔔 Push on</span>
            ) : pstate === 'blocked' ? (
              <span title="Allow notifications in browser settings" style={{ fontSize: 11, color: 'var(--red)', border: '1px solid var(--red)', borderRadius: 4, padding: '4px 10px' }}>Push blocked</span>
            ) : (
              <button onClick={togglePush} disabled={pbusy}
                style={{ fontSize: 11, background: 'var(--gold)', border: 'none', color: 'var(--on-gold)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontWeight: 600 }}>
                {pbusy ? 'Enabling…' : '🔔 Enable push'}
              </button>
            )
          )}
          <button onClick={() => load(true)} title="Refresh"
            style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text3)', fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>↻</button>
        </div>

        <ExportBar compact reportKey="notifications" title="Notifications" sections={[{ name: 'Alerts', columns: [{ key: 'created_at', label: 'When', type: 'date' }, { key: 'severity', label: 'Severity' }, { key: 'title', label: 'Title' }, { key: 'body', label: 'Detail' }, { key: 'is_read', label: 'Read', type: 'int' }], rows: items || [] }]} source={{ endpoint: '/notifications', path: 'items' }} />
        {loading ? (
          <div className="loading">LOADING NOTIFICATIONS…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            🔔 No notifications yet.<br />
            <span style={{ fontSize: 11 }}>Overdue-order alerts and the daily summary will appear here.</span>
          </div>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            {items.map(i => (
              <div key={i.id} onClick={() => clickItem(i)}
                style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)',
                  cursor: i.link ? 'pointer' : 'default',
                  background: i.is_read ? 'var(--bg2)' : 'var(--gold-bg)', display: 'flex', gap: 11 }}>
                <span style={{ width: 9, height: 9, borderRadius: 5, background: sevColor(i.severity), marginTop: 4, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{i.title}</span>
                    {!i.is_read && <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.5, color: '#fff', background: 'var(--red)', borderRadius: 3, padding: '1px 5px' }}>NEW</span>}
                  </div>
                  {i.body && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{i.body}</div>}
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                    {fmtWhen(i.created_at)}{i.is_read ? ' · ✓ read' : ''}{i.link ? '  ·  tap to open →' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
